// The corner grid of the map edge (round 086 card 14, R1812,
// ui-map-interface.md §17.3.05).
//
// Every piece at the map edge takes one slot on the pad inside the edge of
// the chrome, and no piece states an offset of its own. The slots by form
// factor and corner are `CORNER_SLOTS`. The minimap stands in the round
// unit of the console as the round minimap of round 086 card 17 b (lane E2
// of the game UI build). A desktop stands the round unit in the top-left
// corner (R2879); a portrait telephone keeps it bottom left in the console.
//
// The CLEAR MAP is the map inside the chrome: under the readout line, left
// one pad inside each edge. The dock is a box to skip, not an edge (the
// right edge is the screen's). The graticule writes its figures on the
// four edges of the SCREEN, inside the safe inset and with no pad, and
// skips every slot (`fast/chart/pieces/graticule.ts`, R1867, R2373). The
// clear map keeps its pad for the other pieces. Its row figures stand under the readout line, and its top
// row skips the boxes of the chrome that stand on the top edge: the
// readout line and the group bar.
//
// The grid is read from the page, so it costs a layout read: it is read
// once after a change of the console, the window or the stack, and kept
// (§10.3). Nothing here runs per frame.

import { PAD, clusterBox, mirrorDesk, onCluster, safeInsets } from './layout';
import { uiZoom } from './uiscale';

/** A box in CSS px of the viewport, from the top left. */
export interface GridRect { x: number; y: number; w: number; h: number }

/** The clear map and the slots a piece at its edge must skip. */
export interface CornerGrid {
  clear: GridRect;
  skip: readonly GridRect[];
  /** The screen inside the safe inset, with no pad: the graticule stands
   *  on its four edges (R1867, R2373). */
  screen: GridRect;
  /** The boxes of the chrome on the top edge, which the top row skips as
   *  well as `skip`: the readout line and the group bar (R1867). */
  topSkip: readonly GridRect[];
}

/** The four corners of the map edge. */
export type Corner = 'tl' | 'tr' | 'bl' | 'br';

/**
 * The slots of each corner, by form factor (round 086 card 14). A desktop:
 * the round unit top left (the round minimap with the rose, R2879); the
 * notices top right at the edge of the dock; the console bottom left (VIEW,
 * the chat box, and the context slot over them); the chart stack bottom
 * right (the cartouche and the legend card). A telephone: the notices top
 * right, the console bottom left (the round unit), VIEW bottom right; it
 * holds no card of the stack (R1821, R1824). The mirrored desktop (the
 * MIRRORED LAYOUT setting, R2883) flips the desktop left for right: the
 * notices top left, the round unit top right, the chart stack bottom left
 * and the console bottom right, next to the dock. The skip list reads the
 * round unit from `clusterBox`, so the unit beside the rail is skipped too.
 */
export type CornerForm = 'desk' | 'mirror' | 'phone';
export const CORNER_SLOTS: Readonly<Record<CornerForm, Readonly<Record<Corner, readonly string[]>>>> = {
  desk: { tl: ['round'], tr: ['notices'], bl: ['console', 'context'], br: ['stack'] },
  mirror: { tl: ['notices'], tr: ['round'], bl: ['stack'], br: ['console', 'context'] },
  phone: { tl: [], tr: ['notices'], bl: ['console'], br: ['view'] },
};

/** The slot table of the form factor now: the telephone's on the sheet,
 *  else the mirrored or the plain desktop's (R2883). Pure. */
export function cornerForm(sheet: boolean, mirror: boolean): CornerForm {
  return sheet ? 'phone' : mirror ? 'mirror' : 'desk';
}

/**
 * The chrome that stands on the top edge of the screen (R1867): the
 * readout line and the group bar under it. The top row of the graticule
 * skips their boxes, so its figures stand left and right of the line.
 */
const TOP_EDGE = '#topbar, #groupbar';

/** The elements that stand in the slots, by slot, as the page builds them. */
const SLOT_ELEMENTS: Readonly<Record<string, string>> = {
  notices: '#notices',
  console: '#mapbtns, #chat, #rosepress, #mapchrome',
  round: '#rosepress, #mapchrome',
  context: '#selection',
  view: '#mapbtns',
  stack: '#furniture .chart-card',
};

/**
 * The clear map in CSS px of the viewport: one pad inside the edges that
 * the chrome leaves free. `top` is the foot of the readout line and
 * `right` the right edge of the map (the viewport edge), both in viewport px.
 */
export function clearMap(vw: number, vh: number, top: number, right: number, pad: number): GridRect {
  const x = pad, y = top + pad;
  return { x, y, w: Math.max(0, Math.min(vw, right) - pad - x), h: Math.max(0, vh - pad - y) };
}

/** Does the box `a` touch the box `b` grown by `margin` on every side? */
export function touches(a: GridRect, b: GridRect, margin = 0): boolean {
  return a.x < b.x + b.w + margin && a.x + a.w > b.x - margin
    && a.y < b.y + b.h + margin && a.y + a.h > b.y - margin;
}

/** Is the box clear of every slot? */
export function clearOfSlots(box: GridRect, skip: readonly GridRect[], margin = 0): boolean {
  for (const s of skip) if (touches(box, s, margin)) return false;
  return true;
}

/** Adds the box of one element to `out` while it stands on the screen. */
function pushShown(out: GridRect[], el: Element): void {
  const r = el.getBoundingClientRect();
  if (r.width > 0 && r.height > 0) out.push({ x: r.left, y: r.top, w: r.width, h: r.height });
}

let cached: CornerGrid | null = null;
let wired = false;

/** The console, the window or the stack moved: read the grid again. */
export function markCornerGridDirty(): void { cached = null; }

/**
 * Marks the grid stale on every event that moves a slot: a recompute of
 * the console, a resize of the window, the end of a slide of a panel (the
 * arrival of the chrome), and a change of the size of the notices column,
 * the selection panel, the readout line or the group bar (a
 * ResizeObserver). The stack of the chart cards marks it from `restack`.
 * Events, never a poll (§10.3).
 */
function wire(): void {
  if (wired) return;
  wired = true;
  onCluster(markCornerGridDirty);
  window.addEventListener('resize', markCornerGridDirty);
  document.addEventListener('transitionend', (e) => {
    if (e.propertyName === 'transform') markCornerGridDirty();
  });
  if (typeof ResizeObserver === 'undefined') return;
  const ro = new ResizeObserver(markCornerGridDirty);
  for (const id of ['notices', 'selection', 'topbar', 'groupbar', 'dock']) {
    const el = document.getElementById(id);
    if (el) ro.observe(el);
  }
}

/**
 * The corner grid of the page now, in CSS px of the viewport: the clear map
 * from the published lengths of `#ui`, and one box per element that stands
 * in a slot. null with no chrome (an arcade match, a test page).
 */
export function readCornerGrid(sheet: boolean): CornerGrid | null {
  if (cached) return cached;
  if (typeof document === 'undefined') return null;
  const ui = document.getElementById('ui');
  if (!ui) return null;
  wire();
  const z = uiZoom();
  const len = (name: string) => {
    const n = parseFloat(ui.style.getPropertyValue(name));
    return Number.isFinite(n) ? n * z : 0;
  };
  const vw = window.innerWidth, vh = window.innerHeight;
  // The right edge is the screen's on both form factors: the dock is a
  // box to skip and not an edge, so a minimized dock leaves the figures
  // on the screen edge and not in the middle of the map.
  const clear = clearMap(vw, vh, len('--topbar-h'), vw, PAD * z);
  const skip: GridRect[] = [];
  if (!sheet) {
    const dock = document.getElementById('dock');
    if (dock) pushShown(skip, dock);
  }
  const form = cornerForm(sheet, mirrorDesk());
  for (const corner of Object.values(CORNER_SLOTS[form])) {
    for (const slot of corner) {
      for (const el of Array.from(document.querySelectorAll(SLOT_ELEMENTS[slot]))) pushShown(skip, el);
    }
  }
  // The minimap and the rose are pictures on the canvas, so the console
  // states their slots (ui/layout.ts clusterBox).
  for (const unit of ['map', 'rose'] as const) {
    const b = clusterBox(unit);
    if (b.visible && b.w > 0) skip.push({ x: b.x, y: b.y, w: b.w, h: b.h });
  }
  const topSkip: GridRect[] = [];
  for (const el of Array.from(document.querySelectorAll(TOP_EDGE))) pushShown(topSkip, el);
  const ins = safeInsets();
  const screen = { x: ins.l * z, y: ins.t * z, w: Math.max(0, vw - (ins.l + ins.r) * z), h: Math.max(0, vh - (ins.t + ins.b) * z) };
  cached = { clear, skip, screen, topSkip };
  return cached;
}
